/**
 * Client for `/api/contacts/owner-operations/*` (#863): bulk Contact Owner
 * reassignment, the approval requests, and restores. Shared by
 * ReassignOwnerDialog, OwnerRequestsDialog and Contact Detail (#864).
 *
 * Commits and restores run in the background on the server; `waitFor` polls an
 * operation (`?rows=false`, 1 s backing off to 3 s) until it is terminal and
 * then reads the results envelope `{succeeded, failed, results}` LmBulkDialog
 * renders. Polling stops when `isAlive()` turns false (the dialog unmounted or
 * was closed into the background), and gives up after `timeoutMs` (2 min): the
 * run keeps going on the server, whose recovery sweep resumes an interrupted one.
 * `useBackgroundReload` lets a page catch a run closed into the background.
 */
import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

// `|| ''`: an unset URL means same origin (`/api/...`), never `undefined/api/...`.
const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const BASE = `${API}/contacts/owner-operations`;

/** Statuses after which an operation changes no more. */
export const TERMINAL = new Set([
    'committed', 'partially_committed', 'rejected', 'expired', 'restored', 'partially_restored',
]);

export function createOwnerOperationsApi(getAuthHeader) {
    const cfg = () => ({ headers: getAuthHeader ? getAuthHeader() : {} });
    const id = (operationId) => encodeURIComponent(operationId);
    return {
        preview: (contactIds, newOwnerId) =>
            axios.post(`${BASE}/preview`, { contact_ids: contactIds, new_owner_id: newOwnerId }, cfg()).then((r) => r.data),
        commit: (operationId, body) => axios.post(`${BASE}/${id(operationId)}/commit`, body, cfg()).then((r) => r.data),
        request: (body) => axios.post(`${BASE}/request`, body, cfg()).then((r) => r.data),
        get: (operationId, { rows = true } = {}) =>
            axios.get(`${BASE}/${id(operationId)}${rows ? '' : '?rows=false'}`, cfg()).then((r) => r.data),
        list: (status, { limit = 50, cursor } = {}) =>
            axios.get(BASE, { ...cfg(), params: { status, limit, ...(cursor ? { cursor } : {}) } }).then((r) => r.data),
        approve: (operationId, reason) =>
            axios.post(`${BASE}/${id(operationId)}/approve`, { reason }, cfg()).then((r) => r.data),
        reject: (operationId, reason) =>
            axios.post(`${BASE}/${id(operationId)}/reject`, { reason }, cfg()).then((r) => r.data),
        restore: (operationId, reason) =>
            axios.post(`${BASE}/${id(operationId)}/restore`, { reason }, cfg()).then((r) => r.data),
    };
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const STILL_RUNNING_MESSAGE = 'This change is still running in the background and will finish on its own. '
    + 'Check back in a few minutes.';

/**
 * Poll an operation until it is terminal (or `stopAt` includes its status),
 * then return the full view with its results envelope. Past `timeoutMs` it
 * throws an Error flagged `stillRunning` (the dialog offers no retry for it:
 * re-sending the commit could never help a run that is still going).
 */
export async function waitFor(api, operationId, { isAlive = () => true, intervalMs = 1000, maxIntervalMs = 3000,
                                                 stopAt = TERMINAL, timeoutMs = 120000 } = {}) {
    const deadline = Date.now() + timeoutMs;
    let delay = intervalMs;
    let misses = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
        if (!isAlive()) throw new Error('stopped');
        if (Date.now() >= deadline) throw Object.assign(new Error(STILL_RUNNING_MESSAGE), { stillRunning: true });
        let state;
        try {
            // eslint-disable-next-line no-await-in-loop
            state = await api.get(operationId, { rows: false });
            misses = 0;
        } catch (err) {
            const status = err?.response?.status;
            misses += 1;
            if ((status && status < 500) || misses >= 5) throw err;
        }
        if (state && stopAt.has(state.status)) return api.get(operationId);
        // eslint-disable-next-line no-await-in-loop
        await sleep(delay);
        delay = Math.min(maxIntervalMs, Math.round(delay * 1.5));
    }
}

/** How long after a change is closed into the background its page reloads once more. */
export const BACKGROUND_RELOAD_MS = 5000;

/**
 * A change closed into the background ("Close — keep running in background")
 * finishes on the server after its dialog has gone, so reloading only then
 * shows the old data. Returns `reload(opts)`: it calls `fn` now and, when
 * `opts.background` is set, once more `delayMs` later to catch the finished
 * run. The delayed call runs the newest `fn` (the page's filters may have
 * changed meanwhile); a second background reload replaces a pending one, and
 * unmounting cancels it.
 */
export function useBackgroundReload(fn, delayMs = BACKGROUND_RELOAD_MS) {
    const latest = useRef(fn);
    const timer = useRef(null);
    useEffect(() => { latest.current = fn; });
    useEffect(() => {
        const pending = timer;
        return () => clearTimeout(pending.current);
    }, []);
    return useCallback((opts) => {
        latest.current();
        if (!opts?.background) return;
        clearTimeout(timer.current);
        timer.current = setTimeout(() => { timer.current = null; latest.current(); }, delayMs);
    }, [delayMs]);
}

/** "1 of 2 Contact Owner changes left today · resets 12:00 AM (Asia/Manila, organization timezone)". */
export function quotaLine(quota) {
    if (!quota) return '';
    const source = { organization: 'organization timezone', profile: 'your profile timezone', utc: 'UTC' }[quota.zone_source];
    let reset = '';
    try {
        reset = new Date(quota.resets_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: quota.zone || 'UTC' });
    } catch {
        reset = '';
    }
    const where = quota.zone_source === 'utc' ? 'UTC' : `${quota.zone}, ${source}`;
    return `${quota.remaining} of ${quota.limit} Contact Owner changes left today${reset ? ` · resets ${reset} (${where})` : ''}`;
}
